import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeftIcon, TrashIcon } from "@heroicons/react/24/solid";
import PhotoBook5 from "../../assets/img/PhotoBook-8x8.jpg";
import CartService from "../../Services/CartService";
import LandingHeader from "../../Components/Landing/LandingHeader";
import LandingFooter from "../../Components/Landing/LandingFooter";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingImage from "../../assets/img/Spinner.svg";
import CanvasService from "../../Services/CanvasService";
import MyAccountService from "../../Services/MyAccountService";


interface CartItem {
  cartID: number;
  userId: number;
  associatedProductID: number;
  productTypeID: number;
  productName: string;
  quantity: number;
  price: number;
  productSize: string;
  productID: number;
  productImageUrl: string;
}

interface CartSummary {
  subTotal: number;
  discount: number;
  taxAmount: number;
  totalAmount: number;
  deliveryCharges: number;
}

const C = {
  page:         '#EEEEEE',
  surface:      '#FFFFFF',
  surface2:     '#FFFFFF',
  surface3:     '#F3F7FF',
  surface4:     '#EAF1FF',
  border:       '#E5E7EB',
  divider:      '#F1F5F9',
  text1:        '#0B1B3F',
  text2:        '#475569',
  text3:        '#64748B',
  accent:       '#0B5FFF',
  accentHover:  '#0847C7',
  accentSoft:   '#EAF1FF',
  accentBorder: 'rgba(11,95,255,0.3)',
  success:      '#0B5FFF',
  successHover: '#0847C7',
  danger:       '#DC2626',
  dangerSoft:   'rgba(220,38,38,0.08)',
};

function YourCart() {
  const [cartItems, setCartItems]           = useState<CartItem[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [cartSummary, setCartSummary]       = useState<CartSummary | null>(null);
  const [updating, setUpdating]             = useState<number | null>(null);
  const [deleting, setDeleting]             = useState<number | null>(null);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [cartItemCount, setCartItemCount]   = useState(0);

  const navigate  = useNavigate();
  const location  = useLocation();
  const canvasId  = location.state?.canvasId;

  const fetchCartItems = async () => {
    try {
      setLoading(true);
      const response = await CartService.GetCart();
      const { cartItems, subtotal, deliveryCharge, taxAmount, totalAmount } = response;
      setCartItems(cartItems);
      setCartSummary({ subTotal: subtotal, discount: 3.0, taxAmount, totalAmount, deliveryCharges: deliveryCharge });
    } catch {
      setError("Failed to load cart items.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const { CartCount } = await CartService.GetCartCount();
      setCartItemCount(CartCount);
    } catch {
      console.error("Error fetching cart count");
    }
  };

  useEffect(() => { fetchCartItems(); fetchCartCount(); }, []);

  const handleIncreaseQuantity = async (cartID: number, qty: number, productId: number, assocId: number) => {
    setUpdating(cartID);
    const result = await CartService.UpdateCart(productId, cartID, { cartID, associatedProductID: assocId, quantity: qty + 1 });
    setUpdating(null);
    if (result !== "Failed to update cart") { await fetchCartItems(); }
    else { toast.error("Failed to update cart.", { position: "top-center" }); }
  };

  const handleDecreaseQuantity = async (cartID: number, qty: number, productId: number, assocId: number) => {
    if (qty <= 1) return;
    setUpdating(cartID);
    const result = await CartService.UpdateCart(productId, cartID, { cartID, associatedProductID: assocId, quantity: qty - 1 });
    setUpdating(null);
    if (result !== "Failed to update cart") { await fetchCartItems(); }
    else { toast.error("Failed to update cart.", { position: "top-center" }); }
  };

  const handleRemoveItem = async (cartID: number) => {
    setDeleting(cartID);
    const result = await CartService.DeleteCartProject(cartID);
    setDeleting(null);
    fetchCartCount();
    if (result) { await fetchCartItems(); toast.success("Item removed from cart.", { position: "top-center" }); }
  };

  const BackToCanvas = async () => {
    if (!canvasId) {
      toast.error("Redirecting to My Accounts...");
      setTimeout(() => navigate("/myaccounts", { replace: true }), 2000);
    } else {
      const projectData    = await MyAccountService.GetMyProjects("all");
      const projectDetails = projectData.find((p: any) => p.associatedProductID === canvasId);
      CanvasService.GetCanvas(canvasId).then((canvasData: any) => {
        navigate("/canvas", { state: { canvasData, projectDetails } });
      });
    }
  };

  const ProceedToCheckout = () => {
    navigate("/shippingdetails", {
      state: { cartId: cartItems.map(i => i.cartID), specialInstructions },
    });
  };

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: C.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: C.danger, fontSize: 15 }}>{error}</span>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col font-jakarta text-ink antialiased" style={{ background: C.page }}>
      <LandingHeader cartItemsCount={cartItemCount} />

      {/* Progress stepper */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 0" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 0 }}>
          {[
            { step: 1, label: "Your Cart", active: true },
            { step: 2, label: "Shipping Details", active: false },
            { step: 3, label: "Payment Details", active: false },
          ].map((s, i, arr) => (
            <React.Fragment key={s.step}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: s.active ? C.accent : C.surface3,
                  border: `2px solid ${s.active ? C.accent : C.border}`,
                  color: s.active ? "#fff" : C.text3,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                }}>
                  {s.step}
                </div>
                <span style={{ fontSize: 11, fontWeight: s.active ? 600 : 400, color: s.active ? C.text1 : C.text3, whiteSpace: "nowrap" }}>
                  {s.label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <div style={{ width: 80, height: 1, background: C.border, margin: "0 8px", marginBottom: 16 }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, width: "100%", maxWidth: 1200, margin: "0 auto", padding: "40px 24px", display: "flex", gap: 28, alignItems: "flex-start" }}>

        {/* Left: Cart items */}
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          {/* Back + title */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={BackToCanvas}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none",
                color: C.text3, fontSize: 13, cursor: "pointer",
                padding: 0, marginBottom: 16,
                transition: "color 150ms",
              }}
              onMouseEnter={e => (e.currentTarget.style.color = C.text1)}
              onMouseLeave={e => (e.currentTarget.style.color = C.text3)}
            >
              <ChevronLeftIcon style={{ width: 16, height: 16 }} />
              Back to Editor
            </button>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: C.text1, margin: 0 }}>Your Cart</h1>
          </div>

          {/* Items */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
              <img src={LoadingImage} alt="Loading" style={{ width: 48, height: 48 }} />
            </div>
          ) : cartItems.length === 0 ? (
            <div style={{
              borderRadius: 14, border: `1px solid ${C.border}`,
              background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              padding: "48px 24px",
              textAlign: "center",
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🛒</div>
              <p style={{ color: C.text2, fontSize: 15 }}>Your cart is empty.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {cartItems.map(item => (
                <div key={item.cartID} style={{
                  borderRadius: 14,
                  border: `1px solid ${C.border}`,
                  background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  padding: 16,
                  display: "flex", alignItems: "center", gap: 16,
                  transition: "border-color 200ms",
                }}>
                  {/* Thumbnail */}
                  <div style={{
                    flexShrink: 0,
                    width: 110, height: 80,
                    borderRadius: 8,
                    background: C.surface3,
                    border: `1px solid ${C.border}`,
                    overflow: "hidden",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {/* Photobooks have no saved preview, so the URL can be empty or unreachable — fall back to the stock image */}
                    <img
                      src={item.productImageUrl || PhotoBook5}
                      alt={item.productName}
                      onError={e => { if (!e.currentTarget.src.endsWith(PhotoBook5)) e.currentTarget.src = PhotoBook5; }}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text1, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.productName}
                    </div>
                    <div style={{ fontSize: 12, color: C.text3, marginBottom: 10 }}>
                      Size: {item.productSize}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" as const }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: C.text1 }}>
                        ${item.price}
                      </span>

                      {/* Quantity controls */}
                      {updating === item.cartID ? (
                        <img src={LoadingImage} alt="Updating" style={{ width: 28, height: 28 }} />
                      ) : (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <button
                            onClick={() => handleDecreaseQuantity(item.cartID, item.quantity, item.productID, item.associatedProductID)}
                            style={{
                              width: 30, height: 30, borderRadius: "7px 0 0 7px",
                              background: C.surface3, border: `1px solid ${C.border}`, borderRight: "none",
                              color: C.text2, fontSize: 16, fontWeight: 300,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "all 150ms",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.surface4; e.currentTarget.style.color = C.text1; }}
                            onMouseLeave={e => { e.currentTarget.style.background = C.surface3; e.currentTarget.style.color = C.text2; }}
                          >−</button>
                          <div style={{
                            width: 40, height: 30,
                            background: C.surface4, border: `1px solid ${C.accentBorder}`,
                            color: C.text1, fontSize: 13, fontWeight: 600,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {item.quantity}
                          </div>
                          <button
                            onClick={() => handleIncreaseQuantity(item.cartID, item.quantity, item.productID, item.associatedProductID)}
                            style={{
                              width: 30, height: 30, borderRadius: "0 7px 7px 0",
                              background: C.accent, border: `1px solid ${C.accent}`, borderLeft: "none",
                              color: "#fff", fontSize: 16, fontWeight: 300,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "all 150ms",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.accentHover; }}
                            onMouseLeave={e => { e.currentTarget.style.background = C.accent; }}
                          >+</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delete */}
                  <div style={{ flexShrink: 0 }}>
                    {deleting === item.cartID ? (
                      <img src={LoadingImage} alt="Deleting" style={{ width: 28, height: 28 }} />
                    ) : (
                      <button
                        onClick={() => handleRemoveItem(item.cartID)}
                        style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: "none", border: `1px solid ${C.border}`,
                          color: C.text3, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 150ms",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.dangerSoft; e.currentTarget.style.color = C.danger; e.currentTarget.style.borderColor = "rgba(220,38,38,0.3)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = C.text3; e.currentTarget.style.borderColor = C.border; }}
                        title="Remove item"
                      >
                        <TrashIcon style={{ width: 14, height: 14 }} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Special Instructions */}
          <div style={{
            marginTop: 28,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}>
            <div style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${C.divider}`,
              fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
              textTransform: "uppercase" as const, color: C.text3,
            }}>
              Special Instructions
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ fontSize: 12, color: C.text3, marginBottom: 10 }}>
                If you have any special instructions for your order, please leave them here.
              </p>
              <textarea
                value={specialInstructions}
                onChange={e => setSpecialInstructions(e.target.value)}
                placeholder="Add special instructions here..."
                rows={3}
                style={{
                  width: "100%", boxSizing: "border-box" as const,
                  background: C.surface3,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.text1,
                  fontSize: 13,
                  padding: "10px 12px",
                  resize: "vertical" as const,
                  outline: "none",
                  transition: "border-color 150ms",
                  fontFamily: "inherit",
                }}
                onFocus={e => (e.currentTarget.style.borderColor = C.accentBorder)}
                onBlur={e => (e.currentTarget.style.borderColor = C.border)}
              />
            </div>
          </div>
        </div>

        {/* Right: Order Summary */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
            position: "sticky" as const, top: 24,
          }}>
            {/* Summary header */}
            <div style={{
              padding: "14px 18px",
              borderBottom: `1px solid ${C.divider}`,
              background: C.surface,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: C.accentSoft, border: `1px solid ${C.accentBorder}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                  <path d="M9 12h6M9 16h4"/>
                </svg>
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>Order Summary</span>
            </div>

            {/* Summary rows */}
            <div style={{ padding: "16px 18px" }}>
              {[
                { label: "Total Items",      value: String(cartItems.length), bold: false },
                { label: "Sub Total",        value: `$${cartSummary?.subTotal?.toFixed(2) ?? "0.00"}`,        bold: false },
                { label: "Delivery Charges", value: `$${cartSummary?.deliveryCharges?.toFixed(2) ?? "0.00"}`, bold: false },
                { label: "Tax",              value: `$${cartSummary?.taxAmount?.toFixed(2) ?? "0.00"}`,        bold: false },
              ].map(row => (
                <div key={row.label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 0",
                  borderBottom: `1px solid ${C.divider}`,
                }}>
                  <span style={{ fontSize: 13, color: C.text2 }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>{row.value}</span>
                </div>
              ))}

              {/* Total */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 0 4px",
                marginTop: 4,
              }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text1 }}>Total</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: C.accent, letterSpacing: -0.5 }}>
                  ${cartSummary?.totalAmount?.toFixed(2) ?? "0.00"}
                </span>
              </div>
            </div>

            {/* Checkout button */}
            <div style={{ padding: "0 18px 18px" }}>
              <button
                onClick={ProceedToCheckout}
                disabled={loading || cartItems.length === 0}
                style={{
                  width: "100%", height: 44,
                  background: loading || cartItems.length === 0 ? C.surface3 : C.success,
                  border: "none", borderRadius: 999,
                  color: loading || cartItems.length === 0 ? C.text3 : "#fff",
                  fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
                  textTransform: "uppercase" as const,
                  cursor: loading || cartItems.length === 0 ? "not-allowed" : "pointer",
                  transition: "all 150ms",
                }}
                onMouseEnter={e => { if (!loading && cartItems.length > 0) e.currentTarget.style.background = C.successHover; }}
                onMouseLeave={e => { if (!loading && cartItems.length > 0) e.currentTarget.style.background = C.success; }}
              >
                {loading ? "Loading…" : "Proceed to Checkout"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default YourCart;
